/**
 * Debounced MutationObserver factory - creates observers with debounced callbacks
 * @module lib/utils/debouncedObserver
 */

const { logWarn } = require('../logger');

/**
 * Create a MutationObserver with debounced callback execution
 * @param {function} callback - The function to call when mutations are observed
 * @param {object} [options] - Configuration options
 * @param {number} [options.debounceMs=75] - Debounce delay in milliseconds
 * @param {string} [options.errorContext] - Context string for error logging
 * @returns {MutationObserver|null} The observer instance, or null if not available
 */
function createDebouncedObserver(callback, options = {}) {
  if (typeof MutationObserver === 'undefined') {
    return null;
  }
  if (typeof callback !== 'function') {
    return null;
  }

  const {
    debounceMs = 75,
    errorContext = 'Observer callback'
  } = options;

  let queued = false;

  const queueCallback = () => {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      try {
        callback();
      } catch (err) {
        logWarn(`${errorContext} failed`, err);
      }
    }, debounceMs);
  };

  return new MutationObserver(queueCallback);
}

/**
 * Create and start a debounced MutationObserver on the document root
 * @param {function} callback - The function to call when mutations are observed
 * @param {object} [options] - Configuration options
 * @param {number} [options.debounceMs=75] - Debounce delay in milliseconds
 * @param {string} [options.errorContext] - Context string for error logging
 * @param {boolean} [options.childList=true] - Observe child list changes
 * @param {boolean} [options.subtree=true] - Observe entire subtree
 * @returns {MutationObserver|null} The observer instance, or null if setup failed
 */
function createAndStartObserver(callback, options = {}) {
  if (typeof document === 'undefined') {
    return null;
  }

  const root = document.documentElement || document.body;
  if (!root) {
    return null;
  }

  const {
    childList = true,
    subtree = true,
    debounceMs = 75,
    errorContext = 'Observer callback'
  } = options;

  const observer = createDebouncedObserver(callback, { debounceMs, errorContext });
  if (!observer) {
    return null;
  }

  try {
    observer.observe(root, { childList, subtree });
    return observer;
  } catch (err) {
    logWarn('Failed to start MutationObserver', err);
    return null;
  }
}

/**
 * Schedule initial scans with standard timing pattern
 * Handles DOMContentLoaded + delayed rescans for late-loading content
 * @param {function} scanFn - The scan function to call
 * @param {object} [options] - Timing options
 * @param {number[]} [options.delays=[1500, 4500]] - Additional delays for rescans
 */
function scheduleInitialScans(scanFn, options = {}) {
  if (typeof document === 'undefined') return;
  if (typeof scanFn !== 'function') return;

  const { delays = [1500, 4500] } = options;

  // Initial scan
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scanFn());
  } else {
    scanFn();
  }

  // Delayed rescans for late-loading content
  for (const delay of delays) {
    setTimeout(() => scanFn(), delay);
  }
}

module.exports = {
  createDebouncedObserver,
  createAndStartObserver,
  scheduleInitialScans
};
